import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, CalendarCheck, BadgeCheck, CheckCircle2, MapPin } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingCallBar from "@/components/FloatingCallBar";
import Seo from "@/components/Seo";
import { SERVICES, SERVICE_PAGES, SERVICE_FAQS, BRANDS, PHONE_DISPLAY, PHONE_TEL } from "@/constants/site";

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const page = SERVICE_PAGES[slug];

  if (!service || !page) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#FAFAFA] px-4 text-center">
        <h1 className="font-display text-2xl font-bold text-navy">Service not found</h1>
        <Link to="/" className="mt-4 rounded-full bg-rescue px-6 py-3 text-sm font-bold text-white">
          Back to homepage
        </Link>
      </div>
    );
  }

  const Icon = service.icon;

  return (
    <div className="bg-[#FAFAFA] pb-16 md:pb-0" data-testid={`service-page-${slug}`}>
      <Seo
        title={page.metaTitle}
        description={page.metaDesc}
        canonical={`https://gtaappliancerescue.ca/services/${slug}`}
      />
      <Header />
      <main className="pt-36 sm:pt-40">
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-rescue"
              >
                <Icon className="h-3.5 w-3.5" />
                Appliance Repair / Toronto & GTA
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="mt-5 font-display text-4xl font-black leading-[1.05] tracking-tight text-navy sm:text-5xl"
              >
                {page.h1}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
              >
                {page.intro}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="mt-7 flex flex-col gap-3 sm:flex-row"
              >
                <a
                  href={PHONE_TEL}
                  data-testid="service-page-call-button"
                  className="flex items-center justify-center gap-2.5 rounded-full bg-rescue px-8 py-4 text-base font-bold text-white shadow-xl shadow-red-600/25 transition-[background-color,transform] duration-200 hover:bg-rescue-dark hover:-translate-y-0.5"
                >
                  <Phone className="h-5 w-5" /> Call Now: {PHONE_DISPLAY}
                </a>
                <a
                  href="/#request-service"
                  data-testid="service-page-book-button"
                  className="flex items-center justify-center gap-2.5 rounded-full border-2 border-navy bg-white px-8 py-4 text-base font-bold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
                >
                  <CalendarCheck className="h-5 w-5" /> Request Service Online
                </a>
              </motion.div>
              <p className="mt-5 flex items-center gap-1.5 text-sm font-semibold text-slate-600">
                <BadgeCheck className="h-4 w-4 text-rescue" />
                $80 service call — waived when you proceed with the repair · 1-yr parts, 30-day labour warranty
              </p>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative lg:col-span-5"
            >
              <div className="absolute -right-4 -top-4 h-full w-full rounded-[2rem] border-2 border-rescue/30" />
              <img
                src={service.image}
                alt={`${service.name} in Toronto and the GTA`}
                className="relative h-72 w-full rounded-[2rem] object-cover shadow-2xl shadow-navy/25 sm:h-96"
              />
            </motion.div>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-7xl px-4 sm:mt-20 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
            Common {service.name.replace(" Repair", "")} Problems We Fix
          </h2>
          <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {page.problems.map((problem, i) => (
              <motion.div
                key={problem}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-5"
                data-testid={`problem-card-${i}`}
              >
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rescue" />
                <span className="text-sm font-semibold text-navy">{problem}</span>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-navy p-7 sm:p-10">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-red-400">
                  All Major Brands
                </p>
                <div className="mt-4 flex max-w-2xl flex-wrap gap-2">
                  {BRANDS.map((brand) => (
                    <span
                      key={brand}
                      className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-slate-200"
                    >
                      {brand}
                    </span>
                  ))}
                </div>
                <p className="mt-5 flex items-center gap-1.5 text-sm text-slate-300">
                  <MapPin className="h-4 w-4 text-rescue" />
                  Same-day service across Toronto, Mississauga, Brampton, Vaughan, Markham & the entire GTA
                </p>
              </div>
              <a
                href={PHONE_TEL}
                className="flex shrink-0 items-center justify-center gap-2.5 rounded-full bg-rescue px-8 py-4 text-base font-bold text-white shadow-xl shadow-red-600/25 transition-[background-color,transform] duration-200 hover:bg-rescue-dark hover:-translate-y-0.5"
              >
                <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </section>
        <section className="mx-auto mt-16 max-w-4xl px-4 sm:px-6 lg:px-8" data-testid="service-faqs">
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
            {service.name.replace(" Repair", "")} Repair FAQs
          </h2>
          <div className="mt-6 space-y-3">
            {(SERVICE_FAQS[slug] || []).map((faq, i) => (
              <details
                key={faq.q}
                data-testid={`service-faq-${i}`}
                className="rounded-xl border border-slate-200 bg-white px-5 py-4"
              >
                <summary className="cursor-pointer list-none font-display text-base font-bold text-navy">
                  {faq.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{faq.a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
      <div className="mt-20">
        <Footer />
      </div>
      <FloatingCallBar />
    </div>
  );
}
